'use client';

import React, { useMemo, useState } from 'react';
import { INTEGRATIONS, stepData, INTEGRATIONS_STEP, type Integration } from './model';
import { STR } from './strings';

interface Props {
  selected: string[];
  onChange: (slugs: string[]) => void;
}

function groupByCategory(items: Integration[]): [string, Integration[]][] {
  const map = new Map<string, Integration[]>();
  for (const i of items) {
    const list = map.get(i.category) ?? [];
    list.push(i);
    map.set(i.category, list);
  }
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}

/**
 * The integrations step: Hong Kong shortlist grouped by category by default,
 * a "show all" expander, and a search box across every integration. Each item
 * shows the week range PS Global publishes on that integration's page.
 */
export default function IntegrationsPicker({ selected, onChange }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const searchId = 'cxg-int-search';
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const bankFact = stepData(INTEGRATIONS_STEP)?.hk_facts_for_copy?.[0];

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return INTEGRATIONS.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q) ||
          i.slug.includes(q)
      );
    }
    // Selected items stay visible even when they are not on the shortlist.
    return INTEGRATIONS.filter((i) => showAll || i.hk_shortlist || selectedSet.has(i.slug));
  }, [query, showAll, selectedSet]);

  const groups = useMemo(() => groupByCategory(visible), [visible]);
  const searching = query.trim().length > 0;

  const toggle = (slug: string): void => {
    if (selectedSet.has(slug)) onChange(selected.filter((s) => s !== slug));
    else onChange([...selected, slug]);
  };

  return (
    <div className="picker">
      <div className="pk-tools">
        <div className="pk-search">
          <label htmlFor={searchId} className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {STR.integrations.searchLabel}
          </label>
          <input
            id={searchId}
            type="search"
            value={query}
            placeholder={STR.integrations.searchPlaceholder}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {searching && (
            <button type="button" className="pk-clear" onClick={() => setQuery('')} aria-label={STR.integrations.clearSearch}>
              &times;
            </button>
          )}
        </div>
        {!searching && (
          <button type="button" className="btn btn-ghost" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
            {showAll ? STR.integrations.showShortlist : STR.integrations.showAll(INTEGRATIONS.length)}
          </button>
        )}
      </div>

      <div className="pk-meta">
        <span>
          {searching ? STR.integrations.publishedNote : showAll ? STR.integrations.allNote(INTEGRATIONS.length) : STR.integrations.shortlistNote}
        </span>
        <span className={selected.length ? 'sel' : undefined} aria-live="polite">
          {selected.length ? STR.integrations.selected(selected.length) : STR.integrations.none}
        </span>
      </div>

      {groups.length === 0 && <p className="pk-empty">{STR.integrations.noMatches}</p>}

      {groups.map(([category, items]) => (
        <div className="pk-cat" key={category} role="group" aria-label={category}>
          <div className="pk-cat-title">{category}</div>
          <div className="pk-items">
            {items.map((i) => {
              const on = selectedSet.has(i.slug);
              return (
                <button
                  type="button"
                  key={i.slug}
                  role="checkbox"
                  aria-checked={on}
                  className={`pk-item${on ? ' on' : ''}`}
                  onClick={() => toggle(i.slug)}
                >
                  <span className="pk-name">{i.name}</span>
                  <span className="pk-weeks">
                    {STR.integrations.weeks(i.weeks_min, i.weeks_max)}
                    {STR.integrations.status[i.connector_status] && (
                      <span className="st">{` · ${STR.integrations.status[i.connector_status]}`}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {!searching && (
        <p className="pk-fact">
          {STR.integrations.bankFact}{' '}
          <span className="by">
            {bankFact?.url ? (
              <a href={bankFact.url} target="_blank" rel="noopener noreferrer">
                {STR.integrations.bankFactSource}
              </a>
            ) : (
              STR.integrations.bankFactSource
            )}
          </span>
        </p>
      )}
    </div>
  );
}
